import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components';
import { State } from '@hookstate/core';
import { EMPTY_PERSON, Person, useExhibitorDataState } from '../../globalStates/ExhibitorDataState';
import { useLanguageState } from '../../globalStates/LanguageState';
import { addElement, removeElementById } from '../../globalStates/utils';
import { newId } from '../../utils/Id';
import { repositionItem } from '../../utils/Arrays';
import Page from '../../components/Page';
import Button from '../../components/Button';
import TeamSection from './PersonSection';
import { useConfigDataState } from '../../globalStates/ConfigDataState';

const ButtonsBar = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
`

function sortPersons(personStates: State<Person[]>): State<Person>[] {
    const persons = personStates.map(personState => personState)
    return persons.sort((p1, p2) => p1.orderPerson.get() - p2.orderPerson.get())
}

const TeamPage: React.FC<{}> = () => {
    const branding = useLanguageState().getStrings()
    const serviceKeys = useConfigDataState().getServiceKeys()
    const personsLimit = serviceKeys.contactPartner || 0
    const exiState = useExhibitorDataState();
    const personsStates = exiState.getPersons()
    const orderedPersonsStates = useMemo(() => sortPersons(personsStates), [personsStates])

    const canAddPerson = useMemo(() => {
        if (personsStates.length >= personsLimit) return false
        let valid = true;
        personsStates.forEach(personState => {
            const firstName = personState.firstName.get()
            const lastName = personState.lastName.get()
            valid = valid && !!firstName && firstName.length >= 0 && !!lastName && lastName.length >= 0
        });
        return valid
    }, [personsLimit, personsStates])

    const onAddPerson = useCallback(() => {
        addElement<Person>(personsStates, { ...EMPTY_PERSON, id: newId().toString(), orderPerson: personsStates.length + 1 })
    }, [personsStates])

    const onRemovePerson = useCallback((personId: string) => {
        removeElementById<Person>(personsStates, personId)
    }, [personsStates])

    const onOrderChanged = useCallback((personId: string, newIndex: number) => {
        const oldIndex = orderedPersonsStates.findIndex((personState) => personState.id.get() === personId)
        const repositionedPersonStates = repositionItem(orderedPersonsStates, orderedPersonsStates[oldIndex], oldIndex, newIndex)
        repositionedPersonStates.forEach((person, index) => {
            if (person.orderPerson.get() !== index + 1) person.orderPerson.set(index + 1)
        })
    }, [orderedPersonsStates])

    return (
        <Page
            title={branding.configuration.teamPage.pageTitle}
            description={branding.configuration.teamPage.pageDescription}>

            {orderedPersonsStates?.map((personState, index) => (<TeamSection key={personState.id.get()} personState={personState} index={index} onOrderChanged={onOrderChanged} onClickRemove={onRemovePerson} />))}

            <ButtonsBar>
                <Button id='addPerson' caption={branding.configuration.teamPage.btnAddSPerson} inverse onClick={onAddPerson} disabled={!canAddPerson} />
            </ButtonsBar>
        </Page>
    );
}
export default TeamPage